/**
 * A tiny looping animation for the profile editor: the cube hops in place over a scrolling
 * floor and leaves its trail behind, so a kid sees exactly how their character will look.
 */
import type { PlayerSkin } from '@cube-rush/shared';
import { type Ctx2D, cubeAccent, drawCubeIcon, skinColor } from '../../render/icons';
import { drawTrail, type TrailPoint } from './trailArt';

/** Seconds per hop (jump + run). */
const HOP_PERIOD = 0.95;
/** Fraction of the hop spent in the air. */
const AIR_FRACTION = 0.62;
/** Floor speed in cube sizes per second. */
const SPEED = 6.5;

interface Layout {
  cube: number;
  cubeX: number;
  groundY: number;
}

export class ShowcaseScene {
  private time = 0;
  private trail: TrailPoint[] = [];
  private nextId = 1;

  /** Advances the animation; dt = 0 keeps a still picture (reduced motion). */
  step(dt: number, width: number, height: number): void {
    const layout = layoutFor(width, height);
    if (dt <= 0) {
      if (this.trail.length === 0) this.fillStillTrail(layout);
      return;
    }
    this.time += dt;
    const shift = SPEED * layout.cube * dt;
    for (const point of this.trail) point.x -= shift;
    this.trail = this.trail.filter((point) => point.x > -layout.cube);
    this.trail.push({ x: layout.cubeX, y: this.cubeY(layout), id: this.nextId++ });
  }

  draw(ctx: Ctx2D, skin: PlayerSkin, width: number, height: number): void {
    const layout = layoutFor(width, height);
    const { cube, cubeX, groundY } = layout;
    ctx.clearRect(0, 0, width, height);
    this.drawFloor(ctx, layout, width, height);

    const colors = { primary: cubeAccent(skin), secondary: skinColor(skin.secondary) };
    drawTrail(ctx, skin.trail, this.trail, colors, cube, this.time);

    const lift = groundY - cube / 2 - this.cubeY(layout);
    ctx.save();
    ctx.globalAlpha = 0.35 * (1 - Math.min(1, lift / (cube * 2)));
    ctx.fillStyle = '#000';
    ctx.beginPath();
    ctx.ellipse(cubeX, groundY + 3, cube * 0.55, cube * 0.12, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    drawCubeIcon(ctx, skin, cubeX, this.cubeY(layout), cube, {
      rotation: this.rotation(),
      glow: true,
    });
  }

  private drawFloor(ctx: Ctx2D, { cube, groundY }: Layout, width: number, height: number): void {
    const floor = ctx.createLinearGradient(0, groundY, 0, height);
    floor.addColorStop(0, '#3a1f9e');
    floor.addColorStop(1, '#140a3a');
    ctx.fillStyle = floor;
    ctx.fillRect(0, groundY, width, height - groundY);
    // Tile seams scroll with the floor to sell the motion.
    const spacing = cube * 1.6;
    const offset = (this.time * SPEED * cube) % spacing;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let x = -offset; x < width; x += spacing) {
      ctx.moveTo(x, groundY);
      ctx.lineTo(x - cube * 0.6, height);
    }
    ctx.stroke();
    ctx.strokeStyle = '#7ef3ff';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(0, groundY);
    ctx.lineTo(width, groundY);
    ctx.stroke();
  }

  /** Hop phase in [0, 1) while airborne, null while running on the floor. */
  private airPhase(): number | null {
    const phase = (this.time % HOP_PERIOD) / (HOP_PERIOD * AIR_FRACTION);
    return phase < 1 ? phase : null;
  }

  private cubeY({ cube, groundY }: Layout): number {
    const phase = this.airPhase();
    const lift = phase === null ? 0 : cube * 1.35 * 4 * phase * (1 - phase);
    return groundY - cube / 2 - lift;
  }

  /** A quarter turn per hop, like the cube in Geometry Dash. */
  private rotation(): number {
    const hops = Math.floor(this.time / HOP_PERIOD);
    const phase = this.airPhase();
    return (hops + (phase ?? 1)) * (Math.PI / 2);
  }

  private fillStillTrail(layout: Layout): void {
    const y = layout.groundY - layout.cube / 2;
    for (let x = 0; x < layout.cubeX; x += layout.cube / 4) {
      this.trail.push({ x, y, id: this.nextId++ });
    }
  }
}

function layoutFor(width: number, height: number): Layout {
  // Small enough that the highest point of a hop (cube turned 45°, glow) stays in view.
  const cube = Math.max(16, Math.min(height * 0.26, width * 0.16));
  return { cube, cubeX: width * 0.62, groundY: height - Math.max(14, height * 0.17) };
}
